/**
 * AppSidebar component — chat-first unified sidebar.
 * Always renders ChatShellSidebar with app context.
 * Header shows logo + workspace name.
 * Footer shows the user profile.
 */

import { useEffect, useMemo, useRef } from "react"
import { Link, useLocation, useParams } from "react-router-dom"
import { useAuthStore } from "@/store/slices/auth-slice"
import { Code2 } from "lucide-react"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { BarChart3, BookOpen, Bot, FilePlus, FolderKanban, Library, Workflow } from "lucide-react"
import { cn } from "@/lib/utils"
import { usePermissions } from "@/hooks/use-permissions"
import { useArtifactStore } from "@/store/artifact-store"
import { ChatShellSidebar } from "./chat-shell-sidebar"
import { SidebarFooterNav } from "./sidebar-footer-nav"
import { SidebarWorkspaceHeader } from "./sidebar-workspace-header"
import { CoworkModeTabs } from "@/components/cowork/cowork-mode-tabs"
import { isDesktopApp } from "@/lib/desktop"
import { useCoworkStore } from "@/store/cowork-store"

interface AppSidebarProps {
  className?: string
}

/**
 * AppSidebar — always renders the chat-first unified sidebar
 */
export function AppSidebar({ className }: AppSidebarProps) {
  const { state, open, setOpen } = useSidebar()
  const isCollapsed = state === "closed"

  // Focus mode (#1378): when on, collapse the sidebar to zero width via its
  // built-in offcanvas width/left transitions so the chat + canvas expand
  // smoothly. We remember the pre-focus open/collapsed state and restore it on
  // exit, so entering focus never loses the user's sidebar preference.
  const focusMode = useArtifactStore((s) => s.focusMode)
  const restoreOpenRef = useRef<boolean | null>(null)
  useEffect(() => {
    if (focusMode) {
      if (restoreOpenRef.current === null) restoreOpenRef.current = open
      if (open) setOpen(false)
    } else if (restoreOpenRef.current !== null) {
      setOpen(restoreOpenRef.current)
      restoreOpenRef.current = null
    }
  }, [focusMode, open, setOpen])
  const { isAdmin } = usePermissions()
  // CoWork mode (desktop): a dedicated surface — hide the workspace nav + chat
  // list and show the folder workspace instead. Chat mode is unchanged.
  const coworkMode = useCoworkStore((s) => s.mode)
  const coworkActive = isDesktopApp() && coworkMode === "cowork"

  const { appId: routeAppId } = useParams<{ appId: string }>()
  const appId = useMemo(() => {
    if (routeAppId) return routeAppId
    try {
      return (
        localStorage.getItem('taqat_last_active_app_id') ||
        localStorage.getItem('taqat_default_app_id') ||
        undefined
      )
    } catch {
      return undefined
    }
  }, [routeAppId])
  const basePath = `/app/${appId || ''}`
  const newChatPath = `${basePath}/chat`
  const agentsIndexPath = `${basePath}/agents`
  const codingPath = `${basePath}/coding`
  const knowledgeBasePath = `${basePath}/knowledge-base`
  const automationPath = `${basePath}/automations/flows`
  const projectsPath = `${basePath}/projects`
  // Internal-only module — link renders only when the flag is on. The flag is
  // server-driven (delivered on the /auth/session response), so it toggles via
  // a server-env change with no client rebuild.
  const projectsEnabled = useAuthStore().features?.projectsEnabled ?? false
  // Same server-driven channel. Defaults to visible, so a session that has not
  // loaded yet does not blink the entry out and back in.
  const codingHidden = useAuthStore().features?.codingHidden ?? false
  const libraryPath = `${basePath}/library`
  const analyticsPath = `${basePath}/analytics`
  const location = useLocation()
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <Sidebar
      collapsible={focusMode ? "offcanvas" : "icon"}
      className={cn("border-r-0", className)}
    >
      {/* Header — logo + workspace name */}
      <SidebarHeader className="p-3 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        <SidebarWorkspaceHeader />
        {/* CoWork/Chat mode switch (desktop) — pinned to the top, under the brand */}
        <CoworkModeTabs collapsed={isCollapsed} />

        {/* The rule divides workspace navigation from the conversation lists
            below it. Members get it too: their rail has the same two regions,
            and without the divider the nav links read as part of the chat
            list rather than as navigation. */}
        <SidebarMenu className="mt-2 pb-2 border-b border-sidebar-border group-data-[collapsible=icon]:hidden">
          <SidebarMenuItem>
            <SidebarMenuButton
              isActive={isActive(newChatPath)}
              asChild
            >
              <Link to={newChatPath}>
                <FilePlus className="size-4" />
                <span>New Task</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* CoWork mode keeps only "New Task" up top — the workspace nav
              (Agents/Knowledge/Automation/Library/Audit Log) is hidden so the
              rail focuses on the task + CoWork history. */}
          {/* Projects is visible to every member (flag-gated) — non-admins
              only get the boards they've been added to, enforced by the API. */}
          {projectsEnabled && !coworkActive && !isAdmin && (
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={isActive(projectsPath)}
                asChild
              >
                <Link to={projectsPath}>
                  <FolderKanban className="size-4" />
                  <span>Projects</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {/* Agents is visible to every member, not just admins: members own
              the agents they create, and the page also surfaces the read-only
              agents an admin granted them. Admins get the same entry inside
              their fuller workspace nav below. */}
          {!isAdmin && !coworkActive && (
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={isActive(agentsIndexPath)}
                asChild
              >
                <Link to={agentsIndexPath}>
                  <Bot className="size-4" />
                  <span>Agents</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {isAdmin && !coworkActive && (
            <>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive(agentsIndexPath)}
                  asChild
                >
                  <Link to={agentsIndexPath}>
                    <Bot className="size-4" />
                    <span>Agents</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {/* Teammate Code (spec 083). App-scoped like every other entry
                  here, so it unwinds with `appId` when the App concept goes.

                  Omitted entirely when the feature is hidden — not disabled or
                  greyed out, which would still advertise a surface the
                  deployment has chosen not to offer. The routes go with it, so
                  there is nothing left to reach. */}
              {!codingHidden && (
                <SidebarMenuItem>
                  <SidebarMenuButton isActive={isActive(codingPath)} asChild>
                    <Link to={codingPath}>
                      <Code2 className="size-4" />
                      <span>Coding</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive(knowledgeBasePath)}
                  asChild
                >
                  <Link to={knowledgeBasePath}>
                    <BookOpen className="size-4" />
                    <span>Knowledge</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive(automationPath)}
                  asChild
                >
                  <Link to={automationPath}>
                    <Workflow className="size-4" />
                    <span>Automation</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {projectsEnabled && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    isActive={isActive(projectsPath)}
                    asChild
                  >
                    <Link to={projectsPath}>
                      <FolderKanban className="size-4" />
                      <span>Projects</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive(libraryPath)}
                  asChild
                >
                  <Link to={libraryPath}>
                    <Library className="size-4" />
                    <span>Library</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={isActive(analyticsPath)}
                  asChild
                >
                  <Link to={analyticsPath} data-testid="sidebar-analytics-link">
                    <BarChart3 className="size-4" />
                    <span>Analytics</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </>
          )}
        </SidebarMenu>
        <div className="hidden justify-center group-data-[collapsible=icon]:flex">
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger className="size-8" aria-label="Expand app sidebar" />
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        </div>
      </SidebarHeader>

      {/* Content — same chat-first sidebar in both modes; CoWork just swaps the
          chat list for the CoWork task history (folder now lives in the composer). */}
      <SidebarContent>
        <ChatShellSidebar coworkMode={coworkActive} />
      </SidebarContent>

      {/* Footer — settings, help, and user profile */}
      <SidebarFooter className="px-3 pt-3 pb-3 mt-2 gap-1 border-t border-sidebar-border group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        {/* CoWork mode hides the app-nav footer buttons; only the account
            profile below stays (for workspace switch / sign-out). */}
        <SidebarFooterNav collapsed={isCollapsed} navHidden={coworkActive} />
      </SidebarFooter>
    </Sidebar>
  )
}

AppSidebar.displayName = "AppSidebar"
